/**
 * Admin Layout — top-level chrome for the /admin section.
 *
 * Renders a TabList that switches between admin sub-pages (User Activity,
 * Roles, ...). The active tab is derived from the current URL so deep links
 * land on the right tab. User Activity is the default tab, so it sits on bare
 * /admin and Roles has its own /admin/roles path.
 */

import { useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  TabList,
  Tab,
  makeStyles,
  tokens,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { veracityColors } from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { hasGlobalAdmin, canManageDemoPages, canBroadcast, isAnyModuleAdmin } from '@shared/auth';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)',
  },
  tabBar: {
    padding: `0 ${tokens.spacingHorizontalXL}`,
    backgroundColor: '#FFFFFF',
    borderBottom: `1px solid ${veracityColors.firstLight}`,
  },
  body: {
    flex: 1,
  },
});

type TabKey = 'roles' | 'activity' | 'audit' | 'demos' | 'broadcasts' | 'aiModels' | 'geocoding';

const TAB_PATH: Record<TabKey, string> = {
  activity: '/admin',
  aiModels: '/admin/ai-models',
  audit: '/admin/audit',
  broadcasts: '/admin/broadcasts',
  demos: '/admin/demos',
  geocoding: '/admin/geocoding',
  roles: '/admin/roles',
};

function tabFromPath(pathname: string): TabKey {
  if (pathname.startsWith('/admin/ai-models')) return 'aiModels';
  if (pathname.startsWith('/admin/audit')) return 'audit';
  if (pathname.startsWith('/admin/broadcasts')) return 'broadcasts';
  if (pathname.startsWith('/admin/demos')) return 'demos';
  if (pathname.startsWith('/admin/geocoding')) return 'geocoding';
  if (pathname.startsWith('/admin/roles')) return 'roles';
  return 'activity';
}

export function AdminLayout() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useAuth();
  const active = tabFromPath(location.pathname);

  const isGlobalAdmin = hasGlobalAdmin(roles);

  // Which tabs this person may see, in render order. The single-purpose shell
  // roles (Shell.DemoPages, Shell.Broadcast) each open the admin section for
  // THEIR tab only; roles/activity/audit stay global-Admin. Built as a set
  // rather than a chain of "xOnly" booleans because someone can hold more
  // than one: an earlier version computed broadcastOnly as `!demoPagesOnly &&
  // canBroadcast(...)`, which silently hid Broadcasts from anyone holding both.
  // The capability helpers already return true for global Admin.
  const allowedTabs: TabKey[] = [];
  if (isGlobalAdmin) allowedTabs.push('activity', 'roles', 'audit', 'aiModels');
  if (canManageDemoPages(roles)) allowedTabs.push('demos');
  if (canBroadcast(roles)) allowedTabs.push('broadcasts');
  // Geocoding mirrors the BFF gate (isAnyModuleAdmin): it moved here from the
  // Sales admin page 9/04, and module admins keep the access they had there.
  if (isAnyModuleAdmin(roles)) allowedTabs.push('geocoding');

  if (allowedTabs.length === 0) {
    return <Navigate to="/" replace />;
  }
  if (!allowedTabs.includes(active)) {
    return <Navigate to={TAB_PATH[allowedTabs[0]]} replace />;
  }

  const onSelect = (_: SelectTabEvent, data: SelectTabData) => {
    navigate(TAB_PATH[data.value as TabKey]);
  };

  return (
    <div className={styles.root}>
      <div className={styles.tabBar}>
        {/* Alphabetical by label (standing rule 9/04); /admin still lands on
            User Activity via the index route. */}
        <TabList selectedValue={active} onTabSelect={onSelect}>
          {allowedTabs.includes('aiModels') && <Tab value="aiModels">AI Models</Tab>}
          {allowedTabs.includes('audit') && <Tab value="audit">Audit Log</Tab>}
          {allowedTabs.includes('broadcasts') && <Tab value="broadcasts">Broadcasts</Tab>}
          {allowedTabs.includes('demos') && <Tab value="demos">Demo Pages</Tab>}
          {allowedTabs.includes('geocoding') && <Tab value="geocoding">Geocoding</Tab>}
          {allowedTabs.includes('roles') && <Tab value="roles">Roles</Tab>}
          {allowedTabs.includes('activity') && <Tab value="activity">User Activity</Tab>}
        </TabList>
      </div>
      <div className={styles.body}>
        <Outlet />
      </div>
    </div>
  );
}

export default AdminLayout;
